import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  DefaultModalProps,
  ModalSkeleton,
  Checkbox,
  TextField,
} from "../../../components";
import { TimeInLieu } from "../../../API";
import { User, useUser } from "../../../hooks";
import { formatHoursChange, toAWSDate } from "../../../lib";
import DateControl from "../../../components/DatePickers/DateControl";
import {
  checkExistingTimeInLieu,
  mapDefaultValues,
  TimeInLieuFormFields,
} from "./utils";

type Props = DefaultModalProps & {
  title: string;
  onSubmit: ({ data }: { data: TimeInLieuFormFields }) => void;
  testID: string;
  params: {
    row?: TimeInLieu;
    timeInLieus?: TimeInLieu[];
    employeeID?: string;
  };
};

export const RequestModal = ({
  title,
  onSubmit,
  testID,
  params: { row, timeInLieus, employeeID },
  ...rest
}: Props) => {
  const user = useUser() as User;

  const { control, handleSubmit, getValues, setValue } = useForm({
    defaultValues: mapDefaultValues(row, user, employeeID),
  });
  const [saving, setSaving] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  return (
    <ModalSkeleton
      {...rest}
      title={title}
      isForm
      primaryAction={{
        label: "Save",
        pending: saving,
        onSubmit: handleSubmit((data) => {
          if (!getValues("approvedByClient")) {
            showSnackbar(
              "Time in Lieu must by approved by a Client first, before submitting it",
              true
            );
            return;
          }

          if (!data.description || data.description.trim() === "") {
            showSnackbar("Description is required", true);
            return;
          }

          if (
            checkExistingTimeInLieu({
              timeInLieus,
              existingTimeInLieuId: data.id,
              formDateTaken: data.dateTaken,
            })
          ) {
            showSnackbar(
              "Time in Lieu dates already exist in the system",
              true
            );
            return;
          }

          data.dateEarned = toAWSDate(new Date()) as string;

          setSaving(true);
          if (data?.employeeID) {
            return onSubmit({ data } as { data: TimeInLieuFormFields });
          }
          return;
        }),
        testID: "save-request",
      }}
      testID={testID}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(3, 1fr)" },
          alignItems: "start",
          gap: 2,
        }}
      >
        <DateControl
          name="dateTaken"
          control={control}
          label="Time in Lieu Date"
          onChange={(date) => {
            setValue("dateTaken", new Date(date ?? "").toISOString());
            setValue("hoursTaken", date ? 8 : 0);
          }}
        />

        <TextField
          name="hoursTaken"
          control={control}
          label="Hours"
          required
          type="number"
          onChange={(e) =>
            setValue("hoursTaken", formatHoursChange(e.target.value))
          }
        />

        <TextField
          name="description"
          control={control}
          label="Description"
          required
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)" },
          alignItems: "center",
          my: 1,
        }}
      >
        <Checkbox
          required
          control={control}
          name="approvedByClient"
          label="Checked and approved by client?"
        />
        <Typography variant="caption" color="info" mb={2}>
          In case of no client, mark it as approved
        </Typography>
      </Box>
    </ModalSkeleton>
  );
};
